import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { getImageHeight } from '@utils/space';
import SizedImage from '@components/SizedImage';
import { Caption } from '@components/type';
import * as styled from './styles';

function ImageBlock({ imageHeight, imageAlignment, ...imageExtended }) {
  const { credit, image, altText, route, link } = imageExtended;
  if (!get(image, 'asset.fluid')) {
    return null;
  }

  const caption = credit && (
    <styled.SizedCaption>
      <Caption sizes={['small', 'regular']}>
        <em>{credit}</em>
      </Caption>
    </styled.SizedCaption>
  );

  if (imageHeight === 'text') {
    const fluid = get(image, 'asset.fluid');

    return (
      <styled.SizedWrapper $alignment={imageAlignment}>
        <styled.TextWidthImage>
          <styled.Image
            fluid={fluid}
            ratio={1 / (fluid.aspectRatio || 1)}
            alt={altText}
            altText={altText}
            route={route}
            link={link}
          />
          {caption}
        </styled.TextWidthImage>
      </styled.SizedWrapper>
    );
  }

  if (getImageHeight(imageHeight)) {
    return (
      <styled.SizedWrapper $alignment={imageAlignment}>
        <SizedImage
          fluid={get(image, 'asset.fluid')}
          imageHeight={imageHeight}
          alt={altText}
          altText={altText}
          route={route}
          link={link}
        >
          {caption}
        </SizedImage>
      </styled.SizedWrapper>
    );
  }

  const isVerticalImage = get(image, 'asset.fluid.aspectRatio', 1) < 1;
  const fluid = get(image, 'asset.fluid');

  const updatedFluid = Object.fromEntries(
    Object.entries(fluid).map(([key, value]) => [
      key,
      typeof value === 'string'
        ? value.replace(/fit=crop&?/g, '').replace(/&$/, '')
        : value,
    ]),
  );

  return (
    <styled.Wrapper isVerticalImage={isVerticalImage}>
      <styled.Figure>
        <styled.ImageWrapper
          aspectRatio={get(image, 'asset.fluid.aspectRatio', 1)}
          isVerticalImage={isVerticalImage}
        >
          <styled.Image
            fluid={isVerticalImage ? updatedFluid : image.asset.fluid }
            imgStyle={{ objectFit: 'contain' }}
            alt={altText}
            ratio={isVerticalImage ? 1 : 1 / fluid.aspectRatio}
            {...imageExtended}
          />
          {(credit) && (
            <styled.FigCaption>
              <Caption sizes={['small', 'regular']}>
                {credit && <em>{credit}</em>}
              </Caption>
            </styled.FigCaption>
          )}
        </styled.ImageWrapper>
      </styled.Figure>
    </styled.Wrapper>
  );
}

ImageBlock.propTypes = {
  imageHeight: PropTypes.oneOf(['small', 'medium', 'large', 'text']),
  imageAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  credit: PropTypes.string,
  image: PropTypes.object.isRequired,
  altText: PropTypes.string,
};

export default ImageBlock;
